import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { Fragment, useState, Children, cloneElement, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { twMerge } from "tailwind-merge";
import { SafeFormattedMessage } from "./SafeFormattedMessage";

let displayed = [];

export const LoginFormExternalError = ({ reportError, clearError, error, errorName, show }) => {
  useEffect(()=>{
    errorName ??= error;
    if (show && error) {
      reportError(errorName, error);
    } else if (!show || !error) {
      clearError(errorName || error)
    }
    return () => {
      clearError(errorName || error)
    }
  }, [error, show, errorName]);
  return null;
}
LoginFormExternalError.propTypes = {
  show: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  errorName: PropTypes.string,
  reportError: PropTypes.func,
  clearError: PropTypes.func,
}

export const NewFADVLoginForm = ({ onSubmit = () => { }, onChange, requiredFields, children, button, headerContent, submitButtonText, showErrors=true, secondaryButtonText, secondaryButtonHandler, secondaryButtonHref, secondaryButtonId, onEscapeKey, middleContent }) => {
    const [errors, setErrors] = useState({});
    const [externalErrors, setExternalErrors] = useState({});
    const [formData, setFormData] = useState({});
    const [requiredFieldsPresent, setRequiredFieldsPresent] = useState(false);
    const [forceValidateAll, setForceValidateAll] = useState(false);
    const translatableErrorMessages = useSelector(state => state.loginForm.translatableMessages ?? []);
    const loginErrorMessages = useSelector(state => state.loginForm.loginErrorMessages);
    const successMessages = useSelector(state => state.loginForm.successMessages);
    let fieldRefs = new Map();

    const storeDisplayed = (key) => {
        displayed = [...displayed, key];
    };

    // Handle success messages from Redux state
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape" && typeof onEscapeKey === "function") {
                event.preventDefault();
                onEscapeKey();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onEscapeKey]);

    useEffect(() => {
        successMessages?.forEach((loginMessage) => {
            enqueueSnackbar(loginMessage, { variant: "success" });
        });
    }, [successMessages]);
    
    // Handle external errors from Redux state 
    useEffect(() => {
        setExternalErrors({});
        loginErrorMessages?.forEach(({key, message}) => {
            setExternalErrors(prev => ({
                    ...prev,
                    [key]: message,
            }));
        });

        translatableErrorMessages.filter((item) => Boolean(item.message))
        .forEach(({ key, message, context = {}, options = {} }) => {
            // do nothing if message is already displayed
            if (displayed.includes(key)) return;

            if (options.variant === "error") {
                const errorMessage = message ? <SafeFormattedMessage {...message} values={context} /> : null;
                setExternalErrors(prev => ({
                    ...prev,
                    [key]: errorMessage,
                }));
                storeDisplayed(key);    
            }
        });
    }, [loginErrorMessages, translatableErrorMessages]);

    // Whenever formData or requiredFields change, check if all required fields are present and non-empty
    useEffect(() => {
        if (requiredFields === undefined || requiredFields.length === 0) {
            setRequiredFieldsPresent(true);
            return;
        }
        const allFieldsPresent = requiredFields.every(field => {
            const value = formData[field];
            return value !== undefined && value !== null && String(value).trim() !== "";
        });
        setRequiredFieldsPresent(allFieldsPresent);
    }, [formData, requiredFields]);
        
    // Components within the form can call this function to report field-specific errors
    const reportError = (fieldName, errorMessage) => {
        setErrors(prev => ({
            ...prev,
            [fieldName]: errorMessage,
        }));
    };
    // Components within the form can call this function to clear field-specific errors
    const clearError = (fieldName) => {
        setErrors(prev => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });
    };

    const RenderErrors = ({show}) => {
        const errorMessages = Object.values(errors).filter(Boolean).flat();
        const externalErrorMessages = Object.values(externalErrors).filter(Boolean).flat();
        const display = show && (errorMessages.length > 0 || externalErrorMessages.length > 0);
        return (
            <div 
                id="verifyPasswordErrors"
                className={twMerge("bg-error-100 w-full border border-error-100 text-error-700 text-base pr-5 py-4 rounded mb-4", !display && "hidden") }
                role="alert"
                aria-live={display ? "assertive" : "off"}
            >
                <ul className="list-none pl-5 flex flex-col space-y-1">
                    {externalErrorMessages.map((errMsg, index) => (
                        <li key={index}>{errMsg}</li>
                    ))}
                    {Boolean(errorMessages.length && externalErrorMessages.length) && <hr className="w-[75%] self-center text-error-200" />}
                    {errorMessages.map((errMsg, index) => (
                        <li key={index}>{errMsg}</li>
                    ))}
                </ul>
            </div>
        );
    };
    RenderErrors.propTypes = {
        show: PropTypes.bool,
    }

    const handleForgotPasswordClick = () => {
        if (typeof secondaryButtonHandler === "function") {
            secondaryButtonHandler(formData, errors);
            setExternalErrors({});
        }
    };

    const handleInputChange = (e) => {
        // After a submit attempt, return to soft-validation mode while user edits.
        if (forceValidateAll) {
            setForceValidateAll(false);
        }
        const newFormData = Object.assign({}, formData, {[e.target.id]: e.target.value})
        if (typeof onChange === "function") {
            onChange(newFormData, errors);
        }
        setFormData(newFormData);
    };

    const onSubmitHandler = (e) => {
        e.preventDefault();
        // Always show all field errors on submit attempt.
        setForceValidateAll(true);
        if (requiredFieldsPresent) {
            // Trim all string values in formData
            const trimmedFormData = Object.fromEntries(
                Object.entries(formData).map(([key, value]) => [
                    key,
                    typeof value === "string" ? value.trim() : value
                ])
            );
            // Pass errors to the handler so it can decide whether to dispatch.
            onSubmit(trimmedFormData, errors);
            setExternalErrors({});
        } else {
            // Focus first missing required field  
            let firstMissingField = null;
            if (requiredFields && requiredFields.length > 0) {
                requiredFields.forEach(field => {
                    const value = formData[field];
                    if (value === undefined || value === null || String(value).trim() === "") {
                        if (!firstMissingField) firstMissingField = field;
                    }
                });
                if (firstMissingField && fieldRefs.get(firstMissingField)) {
                    fieldRefs.get(firstMissingField).focus();
                }
            }
        }
    }
    const buttonDisabled = !requiredFieldsPresent || (Object.keys(errors).length > 0 && showErrors);
    return (
        <Fragment>
            <h2 className={twMerge("text-2xl text-primary-fg mb-6", headerContent ? "" : "hidden")}>
                {headerContent}
            </h2>
            <div className="relative flex z-10" >
            <RenderErrors show={showErrors} />
            </div>
            <form onSubmit={onSubmitHandler} className="space-y-5">
                <div className="space-y-4">
                    {Children.map(children, child => {
                        if (!React.isValidElement(child)) {
                            return null;
                        }
                        if (child?.props?.id === undefined) {
                            // Need a unique id to track field values and errors
                            return child;
                        }
                        if (child?.props?.externalValue) {
                            formData[child.props.id] = child.props.externalValue;
                        }

                        return cloneElement(child, {
                            reportError,
                            clearError,
                            onChange: handleInputChange,
                            forceValidateAll,
                            inputRef: node => { fieldRefs.set(child.props.id, node); }
                        })
                    })}
                </div>
                {button ??
                    <button
                        id="submit"
                        type="submit"
                        aria-disabled={buttonDisabled}
                        data-disabled={buttonDisabled} // Used by integration tests
                        className={twMerge("w-full bg-accent-600 hover:bg-blue-700 text-white font-medium py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg", buttonDisabled && "hover:bg-neutral-500 bg-neutral-400 cursor-not-allowed")}
                        onClick={onSubmitHandler}
                    >
                        { submitButtonText ?? <FormattedMessage id="login.submit" defaultMessage="Submit" description="A submit button" /> }
                    </button>
                }
                
                {middleContent}
                {secondaryButtonText && (secondaryButtonHandler || secondaryButtonHref) &&
                    <div className="py-4 flex flex-col justify-center items-center">
                        {secondaryButtonHref ? (
                            <a
                                id={secondaryButtonId ?? "secondaryButton"}
                                className="cursor-pointer text-Button-text text-sm font-medium underline"
                                href={secondaryButtonHref}
                                onClick={secondaryButtonHandler ? handleForgotPasswordClick : undefined}
                            >
                                {secondaryButtonText}
                            </a>
                        ) : (
                            <button type="button" id={secondaryButtonId ?? "secondaryButton"} className="cursor-pointer text-Button-text text-sm font-medium underline bg-transparent border-none p-0" onClick={handleForgotPasswordClick}>{secondaryButtonText}</button>
                        )}
                    </div>
                }
            </form>
        </Fragment>
    );
}

NewFADVLoginForm.propTypes = {
    headerContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    submitButtonText: PropTypes.node,
    children: PropTypes.arrayOf(PropTypes.node),
    requiredFields: PropTypes.arrayOf(PropTypes.string),
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    button: PropTypes.node,
    showErrors: PropTypes.bool,
    secondaryButtonText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node
    ]),
    secondaryButtonHandler: PropTypes.func,
    secondaryButtonHref: PropTypes.string,
    secondaryButtonId: PropTypes.string,
    onEscapeKey: PropTypes.func,
    middleContent: PropTypes.node,
}
